import React, { useState, useEffect } from 'react';
import { X, Printer, Download, MessageCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import api from '../../services/api';
import ApproA4 from './ApproA4';
import { repartirTotalSurLignes, formatPU, getDeviseCourante } from '../../utils/format';

// Génère un PDF natif de l'approvisionnement (texte sélectionnable).
// Structure et mise en page miroirs de la maquette ApproA4.
function genererPDF(appro, fournisseur, parametres = {}) {
  const doc = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' });
  const pageW = doc.internal.pageSize.getWidth();
  let y = 14;

  const nomEntreprise = parametres.nom_entreprise || 'Mon entreprise';
  const adresse       = parametres.adresse || '';
  const ville         = parametres.ville || 'Conakry, Guinée';
  const tel           = parametres.telephone || '';
  const email         = parametres.email || '';
  const reference     = `APP-${String(appro.id || '').slice(0, 8).toUpperCase()}`;
  const fmt = (d) => d ? new Date(d).toLocaleDateString('fr-FR') : '—';

  // En-tête entreprise
  doc.setFont('helvetica', 'bold').setFontSize(14);
  doc.text(nomEntreprise, 14, y);
  doc.setFont('helvetica', 'normal').setFontSize(9);
  let yLeft = y + 5;
  if (adresse) { doc.text(adresse, 14, yLeft); yLeft += 4; }
  if (ville)   { doc.text(ville, 14, yLeft); yLeft += 4; }
  if (tel)     { doc.text(`Tél : ${tel}`, 14, yLeft); yLeft += 4; }
  if (email)   { doc.text(`Email : ${email}`, 14, yLeft); yLeft += 4; }

  // Titre droit
  doc.setFont('helvetica', 'bold').setFontSize(16);
  doc.text('BON DE RÉCEPTION', pageW - 14, y, { align: 'right' });
  doc.setFont('helvetica', 'normal').setFontSize(9);
  doc.text(`Réf : ${reference}`, pageW - 14, y + 6, { align: 'right' });
  doc.text(`Date : ${fmt(appro.date)}`, pageW - 14, y + 10, { align: 'right' });
  if (appro.bon_commande_numero) {
    doc.text(`Suite à : ${appro.bon_commande_numero}`, pageW - 14, y + 14, { align: 'right' });
    y = Math.max(yLeft, y + 18);
  } else {
    y = Math.max(yLeft, y + 14);
  }
  doc.setDrawColor(0).setLineWidth(0.4);
  doc.line(14, y, pageW - 14, y);
  y += 6;

  // Bloc fournisseur + émetteur
  const colW = (pageW - 28 - 4) / 2;
  doc.setDrawColor(180).setLineWidth(0.2);
  doc.rect(14, y, colW, 24);
  doc.setFont('helvetica', 'bold').setFontSize(7).setTextColor(100);
  doc.text('FOURNISSEUR', 16, y + 4);
  doc.setTextColor(0).setFontSize(10);
  doc.text(fournisseur?.nom || '—', 16, y + 9);
  doc.setFontSize(8).setFont('helvetica', 'normal').setTextColor(80);
  let yF = y + 13;
  if (fournisseur?.specialite) { doc.text(`Spécialité : ${fournisseur.specialite}`, 16, yF); yF += 4; }
  if (fournisseur?.adresse)    { doc.text(fournisseur.adresse, 16, yF); yF += 4; }
  if (fournisseur?.telephone)  { doc.text(`Tél : ${fournisseur.telephone}`, 16, yF); yF += 4; }

  const x2 = 14 + colW + 4;
  doc.rect(x2, y, colW, 24);
  doc.setFont('helvetica', 'bold').setFontSize(7).setTextColor(100);
  doc.text('RÉCEPTION ENREGISTRÉE PAR', x2 + 2, y + 4);
  doc.setTextColor(0).setFontSize(10);
  doc.text(appro.user_nom || '—', x2 + 2, y + 9);
  doc.setFontSize(8).setFont('helvetica', 'normal').setTextColor(80);
  doc.text('Devise : ' + getDeviseCourante(), x2 + 2, y + 13);
  doc.text(`Stock mis à jour le ${fmt(appro.created_at)}`, x2 + 2, y + 17);
  doc.setTextColor(0);
  y += 30;

  // Tableau
  // Réconciliation affichage : les sous-totaux somment EXACTEMENT au total payé
  // (corrige l'arrondi des prix unitaires issus d'une décomposition de carton).
  const body = repartirTotalSurLignes(
    (appro.lignes || []).filter(l => l.variante_id), appro.cout_total
  ).map((l, i) => {
    const qte = Number(l.quantite || 0);
    const st  = Number(l.total_affiche);
    const nom = [l.produit_nom, l.modele, l.taille, l.couleur && `(${l.couleur})`]
      .filter(Boolean).join(' ');
    return [
      String(i + 1), nom, l.code_variante || '—',
      String(qte),
      formatPU(l.pu_affiche),
      st.toLocaleString('fr-FR'),
    ];
  });

  autoTable(doc, {
    startY: y,
    head: [['#', 'Désignation', 'Réf.', 'Qté', `P.U. (${getDeviseCourante()})`, `Total (${getDeviseCourante()})`]],
    body,
    margin: { left: 14, right: 14 },
    styles: { fontSize: 9, cellPadding: 2 },
    headStyles: { fillColor: [240, 240, 240], textColor: 0, fontStyle: 'bold' },
    columnStyles: {
      0: { halign: 'center', cellWidth: 8 },
      1: { cellWidth: 'auto' },
      2: { cellWidth: 24, font: 'courier', fontSize: 8 },
      3: { halign: 'center', cellWidth: 12 },
      4: { halign: 'right', cellWidth: 26 },
      5: { halign: 'right', cellWidth: 30, fontStyle: 'bold' },
    },
  });

  let y2 = doc.lastAutoTable.finalY + 6;
  const nbArticles = (appro.lignes || []).reduce((s, l) => s + Number(l.quantite || 0), 0);
  const xT = pageW - 14 - 70;
  doc.setFontSize(9).setFont('helvetica', 'normal').setTextColor(80);
  doc.text("Nombre d'articles reçus", xT, y2);
  doc.setTextColor(0);
  doc.text(`${nbArticles} pièces`, pageW - 14, y2, { align: 'right' });
  y2 += 3;
  doc.setLineWidth(0.5).line(xT, y2, pageW - 14, y2);
  y2 += 5;
  doc.setFont('helvetica', 'bold').setFontSize(11);
  doc.text('TOTAL PAYÉ', xT, y2);
  doc.text(`${Number(appro.cout_total || 0).toLocaleString('fr-FR')} ${getDeviseCourante()}`, pageW - 14, y2, { align: 'right' });
  y2 += 8;

  if (appro.notes) {
    doc.setDrawColor(180).setLineWidth(0.2);
    doc.rect(14, y2, pageW - 28, 16);
    doc.setFont('helvetica', 'bold').setFontSize(7).setTextColor(100);
    doc.text('NOTES', 16, y2 + 4);
    doc.setFont('helvetica', 'normal').setFontSize(9).setTextColor(80);
    const notesLines = doc.splitTextToSize(appro.notes, pageW - 32);
    doc.text(notesLines, 16, y2 + 9);
    doc.setTextColor(0);
  }

  const footerY = doc.internal.pageSize.getHeight() - 8;
  doc.setFontSize(8).setTextColor(150);
  doc.text(
    `${nomEntreprise} · Document généré le ${fmt(new Date())} · ${reference}`,
    pageW / 2, footerY, { align: 'center' }
  );
  return doc;
}

export default function ModalImprimerAppro({ appro, fournisseur, onClose }) {
  const [parametres, setParametres] = useState({});
  const reference = `APP-${String(appro?.id || '').slice(0, 8).toUpperCase()}`;

  useEffect(() => {
    api.get('/parametres').then(r => setParametres(r.data || {})).catch(() => {});
  }, []);

  const handleImprimer = () => window.print();

  const handleDownloadPDF = () => {
    try {
      const doc = genererPDF(appro, fournisseur, parametres);
      doc.save(`${reference}.pdf`);
    } catch (err) {
      console.error('Erreur génération PDF:', err);
      toast.error('Erreur lors de la génération du PDF');
    }
  };

  const handleWhatsApp = () => {
    const tel = (fournisseur?.telephone || '').replace(/[^\d]/g, '');
    const total = Number(appro.cout_total || 0).toLocaleString('fr-FR');
    const date = appro.date ? new Date(appro.date).toLocaleDateString('fr-FR') : '';
    const msg =
`*${parametres.nom_entreprise || 'Mon entreprise'}*
Bon de réception ${reference} — ${date}

Total payé : *${total} ${getDeviseCourante()}*

Merci !`;
    const url = tel
      ? `https://wa.me/${tel}?text=${encodeURIComponent(msg)}`
      : `https://wa.me/?text=${encodeURIComponent(msg)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
    if (!tel) toast('Choisis le destinataire dans WhatsApp', { icon: '💬' });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 overflow-y-auto print:p-0 print:bg-white">
      <div className="bg-white rounded-xl w-full max-w-3xl my-4 shadow-2xl print:shadow-none print:max-w-none print:my-0 print:rounded-none">
        <div className="flex flex-wrap gap-2 p-4 border-b border-gray-200 print:hidden">
          <button onClick={handleImprimer}
            className="flex items-center gap-2 bg-yellow-500 hover:bg-yellow-400 text-black font-bold px-4 py-2 rounded-lg transition-colors">
            <Printer size={16} /> Imprimer
          </button>
          <button onClick={handleDownloadPDF}
            className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-500 text-white font-bold px-4 py-2 rounded-lg transition-colors">
            <Download size={16} /> Télécharger PDF
          </button>
          <button onClick={handleWhatsApp}
            className="flex items-center gap-2 bg-green-600 hover:bg-green-500 text-white font-bold px-4 py-2 rounded-lg transition-colors">
            <MessageCircle size={16} /> WhatsApp
          </button>
          <button onClick={onClose}
            className="ml-auto flex items-center gap-2 bg-gray-200 hover:bg-gray-300 text-gray-800 font-bold px-4 py-2 rounded-lg transition-colors">
            <X size={16} /> Fermer
          </button>
        </div>
        <div className="p-4 bg-gray-100 print:p-0 print:bg-white">
          <ApproA4 appro={appro} fournisseur={fournisseur} parametres={parametres} />
        </div>
      </div>
    </div>
  );
}
